<div class="item-fee-rules">
  <div class="rules" cdkDropList (cdkDropListDropped)="onDrop($event)">
    <div class="rules__head">
      <span class="rules__col rules__col--order"></span>
      <span class="rules__col">Match by</span>
      <span class="rules__col">Condition</span>
      <span class="rules__col">Value</span>
      <span class="rules__col">Add. cost</span>
      <span class="rules__col">For every</span>
      <span class="rules__col rules__col--actions"></span>
    </div>

    @if (!groupRules?.length) {
      <div class="rules__empty">
        <strong>No rules yet</strong>
        <span>Add rules to charge different fees for specific products, collections, tags, or vendors.</span>
      </div>
    }

    @for (rule of groupRules?.controls; track rule; let i = $index) {
      <div class="rules__row" cdkDrag [cdkDragData]="i">
        <div class="rules__col rules__col--order">
          <pdq-drag-and-drop class="rules__grip" size="medium" spacing="tight"></pdq-drag-and-drop>
          <span class="rules__arrows">
            <pdq-button
              variant="ghost"
              icon="arrow_up"
              size="small"
              [ariaLabel]="'Move rule ' + (i + 1) + ' up'"
              [disabled]="i === 0"
              (onClick)="moveRule(i, i - 1)"></pdq-button>
            <pdq-button
              variant="ghost"
              icon="arrow_down"
              size="small"
              [ariaLabel]="'Move rule ' + (i + 1) + ' down'"
              [disabled]="i === groupRules.length - 1"
              (onClick)="moveRule(i, i + 1)"></pdq-button>
          </span>
        </div>

        <pdq-dropdown
          [useOverlay]="true"
          class="rules__col"
          [options]="matchByOptions"
          [defaultOption]="rule.value.matchBy | dropdownOption: matchByOptions"
          (onChange)="setValue(rule, 'matchBy', $event.value)"></pdq-dropdown>

        <pdq-dropdown
          [useOverlay]="true"
          class="rules__col"
          [options]="operatorOptionsFor(rule.value.matchBy)"
          [defaultOption]="rule.value.operator | dropdownOption: operatorOptionsFor(rule.value.matchBy)"
          (onChange)="setValue(rule, 'operator', $event.value)"></pdq-dropdown>

        <div class="rules__col rules__value">
          <input
            class="pdq-mat-input rules__value-input"
            [class.is-invalid]="isValueMissing(rule)"
            matInput
            type="text"
            placeholder="Type here"
            [formControl]="rule.controls.value"
            [matAutocomplete]="valueAuto"
            (input)="onValueSearch(rule.value.matchBy, $any($event.target).value)" />
          <img class="rules__value-caret" src="assets/icons/dropdown_down_arrow.svg" alt="" />
          <mat-autocomplete #valueAuto="matAutocomplete" class="pdq-mat-autocomplete-panel">
            @if (valueOptionsUnavailable(rule)) {
              <mat-option disabled>Can't load options right now</mat-option>
            } @else if (!valueOptionsFor(rule).length) {
              <mat-option disabled>No Results Found</mat-option>
            }
            @for (option of valueOptionsFor(rule); track option.value) {
              <mat-option [value]="option.value">{{ option.label }}</mat-option>
            }
          </mat-autocomplete>
        </div>

        <span class="rules__col rules__cost">
          <pdq-input-arrows
            [dirty]="true"
            precision="2"
            min="0"
            width="70"
            widthSize="auto"
            [formControl]="rule.controls.amount"></pdq-input-arrows>
          <pdq-dropdown
            [useOverlay]="true"
            [options]="feeTypeOptions"
            helpTextPosition="bottom"
            [formatter]="feeTypeTrigger"
            [defaultOption]="rule.value.type | dropdownOption: feeTypeOptions"
            (onChange)="setValue(rule, 'type', $event.value)"></pdq-dropdown>
        </span>

        <span
          class="rules__col rules__every"
          [matTooltip]="isPercentRule(rule) ? PERCENT_GROUPING_HINT : ''"
          matTooltipClass="pdq-mat-tooltip">
          <pdq-input-arrows
            [dirty]="true"
            min="1"
            precision="0"
            width="60"
            widthSize="auto"
            [formControl]="rule.controls.step"
            [attr.aria-label]="
              isPercentRule(rule)
                ? 'Units per fee for rule ' + (i + 1) + ', disabled: ' + PERCENT_GROUPING_HINT
                : 'Units per fee for rule ' + (i + 1)
            "
            [disabled]="isPercentRule(rule)"></pdq-input-arrows>
          <pdq-dropdown
            [useOverlay]="true"
            [options]="stepUnitOptions"
            [defaultOption]="rule.value.stepUnit | dropdownOption: stepUnitOptions"
            [disabled]="isPercentRule(rule)"
            (onChange)="setValue(rule, 'stepUnit', $event.value)"></pdq-dropdown>
        </span>

        <span class="rules__col rules__col--actions">
          <pdq-button
            [plain]="true"
            size="small"
            [label]="isExpanded(rule) ? 'Less' : 'More'"
            (onClick)="toggleAdvanced(rule)"></pdq-button>
          <pdq-button
            variant="empty"
            size="small"
            icon="trash"
            iconColor="var(--error-default)"
            (onClick)="deleteRule(i)"
            [ariaLabel]="'Remove rule ' + (i + 1)"></pdq-button>
        </span>

        @if (isValueMissing(rule)) {
          <p class="rules__error">Select a value before this rule can save.</p>
        }

        @if (wasStepReset(rule)) {
          <p class="rules__note">For every reset to 1 for percentage rules</p>
        }

        @if (isExpanded(rule)) {
          <div class="advanced" [class.advanced--single]="isPercentRule(rule)">
            <span class="advanced__eyebrow"
              >Advanced options{{ rule.value.value ? ' · ' + rule.value.value : '' }}</span
            >
            <div class="advanced__field">
              <label>First N items free</label>
              <p class="advanced__help">
                The N cheapest matching items are excluded from this rule.<br />
                @if (isPercentRule(rule)) {
                  On a percentage rule, cheapest-first caps your downside.
                } @else {
                  0 = rule applies to all matching items.
                }
              </p>
              <div class="advanced__control">
                <pdq-input-arrows
                  [dirty]="true"
                  min="0"
                  precision="0"
                  width="70"
                  widthSize="auto"
                  [formControl]="rule.controls.firstNFree"></pdq-input-arrows>
                <span class="advanced__suffix">matching items are free</span>
              </div>
            </div>

            @if (!isPercentRule(rule)) {
              <div class="advanced__field">
                <label>Partial groups</label>
                <p class="advanced__help">
                  @if (isRoundingRelevant(rule)) {
                    How to handle leftovers when items don't fill a full group.
                  } @else {
                    Applies only when "Every" is more than 1. Currently every {{ stepLabel(rule) }}.
                  }
                </p>
                <pdq-dropdown
                  [useOverlay]="true"
                  [options]="roundingOptions"
                  [defaultOption]="rule.value.rounding | dropdownOption: roundingOptions"
                  [disabled]="!isRoundingRelevant(rule)"
                  (onChange)="setValue(rule, 'rounding', $event.value)"></pdq-dropdown>
              </div>
            }
          </div>
        }
      </div>
    }

    <div class="rules__foot">
      <pdq-button [plain]="true" size="small" (onClick)="addRule()">
        <span class="add-rule">
          <span class="add-rule__plus" aria-hidden="true"></span>
          {{ groupRules?.length ? 'New rule' : 'Add first rule' }}
        </span>
      </pdq-button>
    </div>
  </div>
</div>
